import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';

import Matter from 'matter-js';

// ─── Types ───────────────────────────────────────────────────────────

interface GravityContextValue {
  registerBody: (body: Matter.Body, element: HTMLElement) => void;
  unregisterBody: (body: Matter.Body) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

interface GravityProps {
  gravity?: { x: number; y: number };
  className?: string;
  hoverRepel?: boolean;
  repelRadius?: number;
  repelStrength?: number;
  children: React.ReactNode;
}

interface MatterBodyProps {
  matterBodyOptions?: {
    friction?: number;
    restitution?: number;
    density?: number;
    frictionAir?: number;
    frictionStatic?: number;
    [key: string]: unknown;
  };
  x?: string | number;
  y?: string | number;
  angle?: number;
  shape?: 'rectangle' | 'circle';
  initialVelocityX?: number;
  initialVelocityY?: number;
  children: React.ReactNode;
}

// ─── Context ──────────────────────────────────────────────────────────

const GravityContext = createContext<GravityContextValue | null>(null);

function useGravity() {
  const ctx = useContext(GravityContext);
  if (!ctx) {
    throw new Error('MatterBody must be used inside a Gravity component');
  }
  return ctx;
}

// ─── Helpers ─────────────────────────────────────────────────────────

function parsePosition(
  val: string | number | undefined,
  containerSize: number
): number {
  if (val === undefined) return containerSize / 2;
  if (typeof val === 'number') return val;
  if (typeof val === 'string' && val.endsWith('%')) {
    return (parseFloat(val) / 100) * containerSize;
  }
  return parseFloat(val);
}

// ─── Gravity ─────────────────────────────────────────────────────────

export function Gravity({
  gravity = { x: 0, y: 1 },
  className,
  hoverRepel = false,
  repelRadius = 160,
  repelStrength = 0.008,
  children,
}: GravityProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<Matter.Engine | null>(null);
  const runnerRef = useRef<Matter.Runner | null>(null);
  const wallsRef = useRef<Matter.Body[]>([]);
  const bodiesMapRef = useRef(new Map<Matter.Body, HTMLElement>());
  const rafRef = useRef<number>(0);
  const mouseConstraintRef = useRef<Matter.MouseConstraint | null>(null);
  const mousePosRef = useRef<{ x: number; y: number } | null>(null);
  const isHoveringRef = useRef(false);

  // ── Wall creation ──────────────────────────────────────────────────
  const createWalls = useCallback((width: number, height: number) => {
    const thickness = 120;
    return [
      Matter.Bodies.rectangle(
        width / 2,
        height + thickness / 2 - 2,
        width + thickness * 2,
        thickness,
        { isStatic: true, render: { visible: false }, label: 'wall-bottom' }
      ),
      Matter.Bodies.rectangle(
        width / 2,
        -thickness / 2 + 2,
        width + thickness * 2,
        thickness,
        { isStatic: true, render: { visible: false }, label: 'wall-top' }
      ),
      Matter.Bodies.rectangle(
        -thickness / 2 + 2,
        height / 2,
        thickness,
        height + thickness * 2,
        { isStatic: true, render: { visible: false }, label: 'wall-left' }
      ),
      Matter.Bodies.rectangle(
        width + thickness / 2 - 2,
        height / 2,
        thickness,
        height + thickness * 2,
        { isStatic: true, render: { visible: false }, label: 'wall-right' }
      ),
    ];
  }, []);

  // ── Resize handler ─────────────────────────────────────────────────
  const handleResize = useCallback(() => {
    const container = containerRef.current;
    const engine = engineRef.current;
    if (!container || !engine) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    // Remove old walls
    if (wallsRef.current.length > 0) {
      Matter.Composite.remove(engine.world, wallsRef.current);
    }

    // Create new walls
    const newWalls = createWalls(width, height);
    wallsRef.current = newWalls;
    Matter.Composite.add(engine.world, newWalls);
  }, [createWalls]);

  // ── Main effect ────────────────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    // Engine
    const engine = Matter.Engine.create({
      gravity: { x: gravity.x, y: gravity.y, scale: 0.001 },
    });
    engineRef.current = engine;

    // Walls
    const walls = createWalls(width, height);
    wallsRef.current = walls;
    Matter.Composite.add(engine.world, walls);

    // Runner
    const runner = Matter.Runner.create();
    runnerRef.current = runner;
    Matter.Runner.run(runner, engine);

    // DOM sync loop
    const sync = () => {
      bodiesMapRef.current.forEach((element, body) => {
        const rect = element.getBoundingClientRect();
        const elWidth = rect.width;
        const elHeight = rect.height;
        const x = body.position.x - elWidth / 2;
        const y = body.position.y - elHeight / 2;
        const angle = body.angle;
        element.style.transform = `translate(${x}px, ${y}px) rotate(${angle}rad)`;
      });
      rafRef.current = requestAnimationFrame(sync);
    };
    rafRef.current = requestAnimationFrame(sync);

    // Resize
    window.addEventListener('resize', handleResize);

    // Interaction mode setup
    let applyRepel: (() => void) | null = null;
    let handleMouseMove: ((e: MouseEvent) => void) | null = null;
    let handleMouseLeave: (() => void) | null = null;

    if (hoverRepel) {
      handleMouseMove = (e: MouseEvent) => {
        const rect = container.getBoundingClientRect();
        mousePosRef.current = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };
        isHoveringRef.current = true;
      };
      handleMouseLeave = () => {
        mousePosRef.current = null;
        isHoveringRef.current = false;
      };
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseleave', handleMouseLeave);

      applyRepel = () => {
        const mp = mousePosRef.current;
        if (!mp || !isHoveringRef.current) return;
        const allBodies = Matter.Composite.allBodies(engine.world);
        for (const body of allBodies) {
          if (body.isStatic) continue;
          const dx = body.position.x - mp.x;
          const dy = body.position.y - mp.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < repelRadius && dist > 0) {
            const t = 1 - dist / repelRadius;
            const force = repelStrength * t * t;
            const fx = (dx / dist) * force;
            const fy = (dy / dist) * force;
            Matter.Body.applyForce(body, body.position, { x: fx, y: fy });
          }
        }
      };
      Matter.Events.on(engine, 'beforeUpdate', applyRepel);
    } else {
      // Drag mode
      const mouse = Matter.Mouse.create(container);
      mouse.pixelRatio = window.devicePixelRatio || 1;
      const mouseConstraint = Matter.MouseConstraint.create(engine, {
        mouse,
        constraint: {
          stiffness: 0.2,
          render: { visible: false },
        },
      });
      mouseConstraintRef.current = mouseConstraint;
      Matter.Composite.add(engine.world, mouseConstraint);

      const mc = mouseConstraint;
      const originalMouseMove = mc.mouse.mousemove;
      mc.mouse.mousemove = (event: MouseEvent) => {
        if (mc.body) {
          originalMouseMove.call(mc.mouse, event);
        }
      };
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      Matter.Runner.stop(runner);
      Matter.Engine.clear(engine);
      window.removeEventListener('resize', handleResize);
      if (applyRepel) {
        Matter.Events.off(engine, 'beforeUpdate', applyRepel);
      }
      if (handleMouseMove) {
        container.removeEventListener('mousemove', handleMouseMove);
      }
      if (handleMouseLeave) {
        container.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [gravity, createWalls, handleResize, hoverRepel, repelRadius, repelStrength]);

  // ── Register / Unregister ──────────────────────────────────────────
  const registerBody = useCallback(
    (body: Matter.Body, element: HTMLElement) => {
      const engine = engineRef.current;
      if (engine) {
        Matter.Composite.add(engine.world, body);
      }
      bodiesMapRef.current.set(body, element);
    },
    []
  );

  const unregisterBody = useCallback((body: Matter.Body) => {
    const engine = engineRef.current;
    if (engine) {
      Matter.Composite.remove(engine.world, body);
    }
    bodiesMapRef.current.delete(body);
  }, []);

  return (
    <GravityContext.Provider
      value={{ registerBody, unregisterBody, containerRef }}
    >
      <div
        ref={containerRef}
        className={className}
        style={{
          position: 'relative',
          overflow: 'hidden',
          touchAction: 'none',
        }}
      >
        {children}
      </div>
    </GravityContext.Provider>
  );
}

// ─── MatterBody ──────────────────────────────────────────────────────

export function MatterBody({
  matterBodyOptions = {},
  x,
  y,
  angle = 0,
  shape = 'rectangle',
  initialVelocityX,
  initialVelocityY,
  children,
}: MatterBodyProps) {
  const gravity = useGravity();
  const elementRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<Matter.Body | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    const container = gravity.containerRef.current;
    if (!element || !container) return;

    const timer = setTimeout(() => {
      const elRect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const elWidth = elRect.width || (shape === 'circle' ? 64 : 100);
      const elHeight = elRect.height || (shape === 'circle' ? 64 : 40);

      const posX = parsePosition(x, containerRect.width);
      const posY = parsePosition(y, containerRect.height);

      let body: Matter.Body;
      if (shape === 'circle') {
        const radius = Math.max(elWidth, elHeight) / 2;
        body = Matter.Bodies.circle(posX, posY, radius, {
          ...matterBodyOptions,
          render: { visible: false },
        });
      } else {
        body = Matter.Bodies.rectangle(posX, posY, elWidth, elHeight, {
          ...matterBodyOptions,
          angle: (angle * Math.PI) / 180,
          render: { visible: false },
        });
      }
      bodyRef.current = body;

      // Apply initial velocity for auto-animation
      if (initialVelocityX !== undefined || initialVelocityY !== undefined) {
        Matter.Body.setVelocity(body, {
          x: initialVelocityX ?? 0,
          y: initialVelocityY ?? 0,
        });
      }

      gravity.registerBody(body, element);
      setReady(true);
    }, 50);

    return () => {
      clearTimeout(timer);
      if (bodyRef.current) {
        gravity.unregisterBody(bodyRef.current);
        bodyRef.current = null;
      }
    };
  }, [gravity, x, y, angle, matterBodyOptions, shape, initialVelocityX, initialVelocityY]);

  return (
    <div
      ref={elementRef}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        opacity: ready ? 1 : 0,
        transition: 'opacity 0.3s ease',
        willChange: 'transform',
        cursor: 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      {children}
    </div>
  );
}